import React from 'react';
import _ from 'lodash';
import { makeStyles } from '@material-ui/core/styles';
import NumberFormat from 'react-number-format';
import { IUser, IUserStats } from '@pvplevelup/core/dist/interfaces/IUser';
import { Grid, Paper, Box, colors, Stepper, Step, StepLabel, LinearProgress, useMediaQuery } from '@material-ui/core';
import { CircularProgressWithLabel } from 'components/Progress/CircularWithLabel';
import { normalize } from 'utilities/normalize'
import ReactResizeDetector from 'react-resize-detector';
import {ExpandLess,ExpandMore, Remove, EmojiEvents, ShowChart} from '@material-ui/icons'
import { Sword } from 'components/SvgIcons/Sword';
import { Cross } from 'components/SvgIcons/Cross';
import { Crosshair } from 'components/SvgIcons/Crosshair';
import { IGameFeed } from '@pvplevelup/core/dist/queries/game/gameFeed';
import { IUserMapFeed } from '@pvplevelup/core/dist/queries/user/user';
import { MapThumbnailIcons } from 'utilities/map_icons';
import { ZoneInformation } from '@pvplevelup/core/dist/interfaces/maps/IMaps';
import { hhmmss } from '@pvplevelup/core/dist/utility/time';


interface Props {
  user: IUser | null;
  stats: IUserStats | undefined;
  classStats: IUserStats | undefined;
  mapStats: IUserMapFeed | undefined;
  gamesByUser: IGameFeed | undefined;
}

interface DiffProps{
  stat: number | undefined;
  classStat: number | undefined;
  fontSize?: string;
  iconSize?: string;
  precision?: number;
  isTime?: boolean;
}


const useStyles = makeStyles((theme) => {
	return {
    stepper:{
      background: 'none'
    },
    cardTitle:{
      paddingBottom: '0.75em',
      fontSize: '0.75rem',
      fontWeight: 700,
      letterSpacing: '0.08em',
      opacity: 0.7,
    },
    progress:{
      height:10
    },
    secondBar:{
      height:10,
      backgroundColor:colors.red[900]
    }
	};
});

function round(value:number, decimals:number) {
  //@ts-ignore
  return Number(Math.round(value+'e'+decimals)+'e-'+decimals);
}

export const largeScreenOffset = 248;
export const smallScreenOffset = 325;
export const breakPoint = 1250;

export const DifferenceComponent: React.FC<DiffProps> = ({stat,classStat,fontSize,precision,iconSize,isTime}) => {
  let diff:any = round((stat || 0) - (classStat || 0),(precision == undefined) ? 2 : precision);
  let text = diff;
  if(isTime)
    text = hhmmss(Math.abs(diff),false);
  return <div style={{fontSize:fontSize ? fontSize : '1em',color:(diff > 0) ? colors.blue[600] : (diff < 0) ? colors.orange[800] : 'initial'}}>{diff > 0 ? <ExpandLess style={{fontSize:iconSize ? iconSize : '1.2em'}} /> : diff < 0 ? <ExpandMore  style={{fontSize:iconSize ? iconSize : '1.2em'}} /> : <Remove  style={{fontSize:iconSize ? iconSize : '1.2em'}} />} {isTime ? text : <NumberFormat value={text} thousandSeparator={true} decimalScale={(precision == undefined) ? 2 : precision} displayType="text" />}</div>
}

let fontColor = '#A6B0BD';
let borderBottom = "1px dotted #484E5B";

const UserStats: React.FC<Props> = ({user,stats,classStats,mapStats,gamesByUser}) => {
  const classes = useStyles();
  let mostPlayedMap = mapStats?.maps && mapStats.maps.length > 0 ? MapThumbnailIcons[mapStats?.maps[0].zone || ""][mapStats?.maps[0].map || ""] : "";
  let mostSuccessfulMaps = _.reverse(_.sortBy(_.take(_.reverse(_.sortBy(mapStats?.maps,(map)=>map.played)),10),(map)=>map.wr));
  let mostSuccessfulMap = mostSuccessfulMaps && mostSuccessfulMaps.length > 0 ? MapThumbnailIcons[mostSuccessfulMaps[0].zone || ""][mostSuccessfulMaps[0].map || ""] : "";

  const matches = useMediaQuery(`(max-width:${breakPoint}px)`);
 
	return (
    <div className="user-stats-grid" style={{minHeight:"100%"}}>
      <Grid container spacing={2} alignItems={"baseline"}>
        <Grid container item xs={12} sm={12} md={12} lg={(gamesByUser != undefined && mapStats != undefined) ? 8 : 12} spacing={2}>

          <Grid item xs={12} sm={12} md={4}>
            <Paper>
              <Box display="grid" p={1} gridTemplateRows={"auto 1fr auto auto auto auto auto"} minHeight={348}>
                <Box className={classes.cardTitle}>WIN RATE</Box>
                <Box paddingBottom="1em" fontWeight="bold" display="grid" gridTemplateColumns={"auto 1fr"} gridGap={"1em"}>
                  <EmojiEvents style={{fontSize:"5em",color:fontColor}} />
                  <Box>
                    <div style={{fontSize:'2em'}}>{((stats?.wr || 0) * 100).toFixed(2)}%</div>
                    <DifferenceComponent stat={(stats?.wr || 0) * 100} classStat={(classStats?.wr || 0) * 100} precision={2} fontSize={"1.4em"} iconSize={"1em"} />
                  </Box>
                </Box>
                <Box paddingBottom="5px" marginBottom="1em" display="grid" gridTemplateColumns="auto auto" color={fontColor} borderBottom={borderBottom}>
                  <Box fontWeight="bold">PLAYED</Box>
                  <Box justifySelf="end"><NumberFormat value={(stats?.wins || 0) + (stats?.loses || 0) + (stats?.ties || 0)} thousandSeparator={true} decimalScale={0} displayType="text" /></Box>
                </Box>
                <Box paddingBottom="5px" marginBottom="1em" display="grid" gridTemplateColumns="auto auto" color={fontColor} borderBottom={borderBottom}>
                  <Box fontWeight="bold">WON</Box>
                  <Box justifySelf="end"><NumberFormat value={(stats?.wins || 0)} thousandSeparator={true} decimalScale={0} displayType="text" /></Box>
                </Box>
                <Box paddingBottom="5px" marginBottom="1em" display="grid" gridTemplateColumns="auto auto" color={fontColor} borderBottom={borderBottom}>
                  <Box fontWeight="bold">LOST</Box>
                  <Box justifySelf="end"><NumberFormat value={(stats?.loses || 0)} thousandSeparator={true} decimalScale={0} displayType="text" /></Box>
                </Box>
                <Box paddingBottom="5px" marginBottom="1em" display="grid" gridTemplateColumns="auto auto" color={fontColor} borderBottom={borderBottom}>
                  <Box fontWeight="bold">DREW</Box>
                  <Box justifySelf="end"><NumberFormat value={(stats?.ties || 0)} thousandSeparator={true} decimalScale={0} displayType="text" /></Box>
                </Box>
                <Box paddingBottom="5px" marginBottom="1em" display="grid" gridTemplateColumns="auto auto" color={fontColor} borderBottom={borderBottom}>
                  <Box fontWeight="bold">TIME IN OBJECTIVE</Box>
                  <Box justifySelf="end" textAlign="end">
                    {hhmmss(Math.round(stats?.avg_timeInObjective || 0),false)}
                    <DifferenceComponent isTime={true} stat={Math.round(stats?.avg_timeInObjective || 0)} classStat={Math.round(classStats?.avg_timeInObjective || 0)} precision={0} fontSize={"0.8em"} iconSize={"1em"} />
                  </Box>
                </Box>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={12} md={4}>
            <Paper>
              <Box display="grid" p={1} gridTemplateRows={"auto 1fr auto auto auto auto auto"} minHeight={348}>
                <Box className={classes.cardTitle}>K/D</Box>
                <Box paddingBottom="1em" fontWeight="bold" display="grid" gridTemplateColumns={"auto 1fr"} gridGap={"1em"}>
                  <Sword style={{width:60,fill:fontColor}} />
                  <Box>
                    <div style={{fontSize:'2em'}}><NumberFormat value={(stats?.kd || 0)} thousandSeparator={true} decimalScale={2} displayType="text" /></div>
                    <DifferenceComponent stat={stats?.kd} classStat={classStats?.kd} precision={2} fontSize={"1.4em"} iconSize={"1em"} />
                  </Box>
                </Box>
                <Box paddingBottom="5px" marginBottom="1em" display="grid" gridTemplateColumns="auto auto" color={fontColor} borderBottom={borderBottom}>
                  <Box fontWeight="bold">EFFECTIVE KILLS</Box>
                  <Box justifySelf="end"><NumberFormat value={(stats?.eKills || 0)} thousandSeparator={true} decimalScale={0} displayType="text" /></Box>
                </Box>
                <Box paddingBottom="5px" marginBottom="1em" display="grid" gridTemplateColumns="auto auto" color={fontColor} borderBottom={borderBottom}>
                  <Box fontWeight="bold">KILLS</Box>
                  <Box justifySelf="end"><NumberFormat value={(stats?.kills || 0)} thousandSeparator={true} decimalScale={0} displayType="text" /></Box>
                </Box>
                <Box paddingBottom="5px" marginBottom="1em" display="grid" gridTemplateColumns="auto auto" color={fontColor} borderBottom={borderBottom}>
                  <Box fontWeight="bold">DEATHS</Box>
                  <Box justifySelf="end"><NumberFormat value={(stats?.deaths || 0)} thousandSeparator={true} decimalScale={0} displayType="text" /></Box>
                </Box>
                <Box paddingBottom="5px" marginBottom="1em" display="grid" gridTemplateColumns="auto auto" color={fontColor} borderBottom={borderBottom}>
                  <Box fontWeight="bold">ASSISTS</Box>
                  <Box justifySelf="end"><NumberFormat value={(stats?.assists || 0)} thousandSeparator={true} decimalScale={0} displayType="text" /></Box>
                </Box>
                <Box paddingBottom="5px" marginBottom="1em" display="grid" gridTemplateColumns="auto auto" color={fontColor} borderBottom={borderBottom}>
                  <Box fontWeight="bold">FINISHING BLOWS</Box>
                  <Box justifySelf="end"><NumberFormat value={(stats?.fBlows || 0)} thousandSeparator={true} decimalScale={0} displayType="text" /></Box>
                </Box>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={12} md={4}>
            <Paper>
              <Box display="grid" p={1} gridTemplateRows={"auto 1fr max-content max-content max-content"} minHeight={348}>
                <Box className={classes.cardTitle}>CROWD CONTROLS</Box>
                <Box paddingBottom="1em" fontWeight="bold" display="grid" gridTemplateColumns={"auto 1fr"} gridGap={"1em"}>
                  <Cross style={{width:60,height:60,fill:fontColor}} />
                  <Box>
                    <div style={{fontSize:'2em'}}><NumberFormat value={(stats?.ccs || 0)} thousandSeparator={true} decimalScale={0} displayType="text" /></div>
                    <DifferenceComponent stat={stats?.ccs} classStat={classStats?.ccs} precision={0} fontSize={"1.4em"} iconSize={"1em"} />
                  </Box>
                </Box>
                <Box paddingBottom="5px" marginBottom="1em" display="grid" gridTemplateColumns="auto auto" color={fontColor} borderBottom={borderBottom}>
                  <Box fontWeight="bold">CCS</Box>
                  <Box justifySelf="end"><NumberFormat value={(stats?.ccs || 0)} thousandSeparator={true} decimalScale={0} displayType="text" /></Box>
                </Box>
                <Box paddingBottom="5px" marginBottom="1em" display="grid" gridTemplateColumns="auto auto" color={fontColor} borderBottom={borderBottom}>
                  <Box fontWeight="bold">OFFENSIVE CCS</Box>
                  <Box justifySelf="end"><NumberFormat value={(stats?.oCCs || 0)} thousandSeparator={true} decimalScale={0} displayType="text" /></Box>
                </Box>
                <Box paddingBottom="5px" marginBottom="1em" display="grid" gridTemplateColumns="auto auto" color={fontColor} borderBottom={borderBottom}>
                  <Box fontWeight="bold">DEFENSIVE CCS</Box>
                  <Box justifySelf="end"><NumberFormat value={(stats?.dCCs || 0)} thousandSeparator={true} decimalScale={0} displayType="text" /></Box>
                </Box>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={12} md={6}>
            <Paper>
              <Box display="grid" p={1} gridTemplateRows={"auto 1fr auto auto auto"}>
                <Box className={classes.cardTitle}>AVERAGE DPS</Box>
                <ReactResizeDetector handleWidth handleHeight>
                  {({width}:{width:number,height:number})=>
                    <Box justifySelf="center">
                      <CircularProgressWithLabel thickness={1.5} size={200} value={(normalize(stats?.avg_dps || 0,0,(classStats?.avg_dps || 0)) * 100).clamp(0,100)}>
                        <div style={{textAlign:'center'}}>
                          <div style={{fontSize:'3em'}}>{(stats?.avg_dps || 0).toFixed(2)}</div>
                          <DifferenceComponent stat={stats?.avg_dps} classStat={classStats?.avg_dps} precision={0} fontSize={"1.4em"} iconSize={"1em"} />
                        </div>
                      </CircularProgressWithLabel>
                    </Box>
                  }
                </ReactResizeDetector>
                <Box paddingBottom="5px" marginBottom="1em" display="grid" gridTemplateColumns="auto auto" color={fontColor} borderBottom={borderBottom}>
                  <Box fontWeight="bold">DAMAGE</Box>
                  <Box justifySelf="end" textAlign="end">
                    <NumberFormat value={(stats?.avg_damage || 0)} thousandSeparator={true} decimalScale={0} displayType="text" />
                    <DifferenceComponent stat={stats?.avg_damage} classStat={classStats?.avg_damage} precision={0} fontSize={"0.8em"} iconSize={"1em"} />
                  </Box>
                </Box>
                <Box paddingBottom="5px" marginBottom="1em" display="grid" gridTemplateColumns="auto auto" color={fontColor} borderBottom={borderBottom}>
                  <Box fontWeight="bold">DAMAGE TAKEN</Box>
                  <Box justifySelf="end" textAlign="end">
                    <NumberFormat value={(stats?.avg_dTaken || 0)} thousandSeparator={true} decimalScale={0} displayType="text" />
                    <DifferenceComponent stat={stats?.avg_dTaken} classStat={classStats?.avg_dTaken} precision={0} fontSize={"0.8em"} iconSize={"1em"} />
                  </Box>
                </Box>
                <Box paddingBottom="5px" marginBottom="1em" display="grid" gridTemplateColumns="auto auto" color={fontColor} borderBottom={borderBottom}>
                  <Box fontWeight="bold">DAMAGE TAKEN PER SECOND</Box>
                  <Box justifySelf="end" textAlign="end">
                    <NumberFormat value={(stats?.avg_dtps || 0)} thousandSeparator={true} decimalScale={2} displayType="text" />
                    <DifferenceComponent stat={stats?.avg_dtps} classStat={classStats?.avg_dtps} precision={2} fontSize={"0.8em"} iconSize={"1em"} />
                  </Box>
                </Box>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={12} md={6}>
            <Paper style={{height:'100%'}}>
              <Box display="grid" p={1} gridTemplateRows={"auto 1fr auto auto"}>
                <Box className={classes.cardTitle}>AVERAGE HPS</Box>
                <ReactResizeDetector handleWidth handleHeight>
                  {({width}:{width:number,height:number})=>
                    <Box justifySelf="center">
                      <CircularProgressWithLabel thickness={1.5} size={200} value={(normalize(stats?.avg_hps || 0,0,(classStats?.avg_hps || 0)) * 100).clamp(0,100)}>
                        <div style={{textAlign:'center'}}>
                          <div style={{fontSize:'3em'}}>{(stats?.avg_hps || 0).toFixed(2)}</div>
                          <DifferenceComponent stat={stats?.avg_hps} classStat={classStats?.avg_hps} precision={0} fontSize={"1.4em"} iconSize={"1em"} />
                        </div>
                      </CircularProgressWithLabel>
                    </Box>
                  }
                </ReactResizeDetector>
                <Box paddingBottom="5px" marginBottom="1em" display="grid" gridTemplateColumns="auto auto" color={fontColor} borderBottom={borderBottom}>
                  <Box fontWeight="bold">Healing</Box>
                  <Box justifySelf="end" textAlign="end">
                    <NumberFormat value={(stats?.avg_heal || 0)} thousandSeparator={true} decimalScale={0} displayType="text" />
                    <DifferenceComponent stat={stats?.avg_heal} classStat={classStats?.avg_heal} precision={0} fontSize={"0.8em"} iconSize={"1em"} />
                  </Box>
                </Box>
                <Box paddingBottom="5px" marginBottom="1em" display="grid" gridTemplateColumns="auto auto" color={fontColor} borderBottom={borderBottom}>
                  <Box fontWeight="bold">Effective Big Heals</Box>
                  <Box justifySelf="end" textAlign="end">
                    <NumberFormat value={(stats?.avg_eBigHeal || 0)} thousandSeparator={true} decimalScale={0} displayType="text" />
                    <DifferenceComponent stat={stats?.avg_eBigHeal} classStat={classStats?.avg_eBigHeal} precision={0} fontSize={"0.8em"} iconSize={"1em"} />
                  </Box>
                </Box>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={12} md={4}>
            <Paper>
              <Box display="grid" p={1} gridTemplateRows={"auto 1fr auto auto auto auto auto"} minHeight={412}>
                <Box className={classes.cardTitle}>AVERAGE K/D</Box>
                <Box paddingBottom="1em" fontWeight="bold" display="grid" gridTemplateColumns={"auto 1fr"} gridGap={"1em"}>
                  <Sword style={{width:60,fill:fontColor}} />
                  <Box>
                    <div style={{fontSize:'2em'}}><NumberFormat value={(stats?.game_kd || 0)} thousandSeparator={true} decimalScale={2} displayType="text" /></div>
                    <DifferenceComponent stat={stats?.game_kd} classStat={classStats?.game_kd} precision={2} fontSize={"1.4em"} iconSize={"1em"} />
                  </Box>
                </Box>
                <Box paddingBottom="5px" marginBottom="1em" display="grid" gridTemplateColumns="auto auto" color={fontColor} borderBottom={borderBottom}>
                  <Box fontWeight="bold">EFFECTIVE KILLS</Box>
                  <Box justifySelf="end" textAlign="end">
                    <NumberFormat value={(stats?.avg_eKills || 0)} thousandSeparator={true} decimalScale={0} displayType="text" />
                    <DifferenceComponent stat={stats?.avg_eKills} classStat={classStats?.avg_eKills} precision={0} fontSize={"0.8em"} iconSize={"1em"} />
                  </Box>
                </Box>
                <Box paddingBottom="5px" marginBottom="1em" display="grid" gridTemplateColumns="auto auto" color={fontColor} borderBottom={borderBottom}>
                  <Box fontWeight="bold">KILLS</Box>
                  <Box justifySelf="end" textAlign="end">
                    <NumberFormat value={(stats?.avg_kills || 0)} thousandSeparator={true} decimalScale={0} displayType="text" />
                    <DifferenceComponent stat={stats?.avg_kills} classStat={classStats?.avg_kills} precision={0} fontSize={"0.8em"} iconSize={"1em"} />
                  </Box>
                </Box>
                <Box paddingBottom="5px" marginBottom="1em" display="grid" gridTemplateColumns="auto auto" color={fontColor} borderBottom={borderBottom}>
                  <Box fontWeight="bold">DEATHS</Box>
                  <Box justifySelf="end" textAlign="end">
                    <NumberFormat value={(stats?.avg_deaths || 0)} thousandSeparator={true} decimalScale={0} displayType="text" />
                    <DifferenceComponent stat={stats?.avg_deaths} classStat={classStats?.avg_deaths} precision={0} fontSize={"0.8em"} iconSize={"1em"} />
                  </Box>
                </Box>
                <Box paddingBottom="5px" marginBottom="1em" display="grid" gridTemplateColumns="auto auto" color={fontColor} borderBottom={borderBottom}>
                  <Box fontWeight="bold">ASSISTS</Box>
                  <Box justifySelf="end" textAlign="end">
                    <NumberFormat value={(stats?.avg_assists || 0)} thousandSeparator={true} decimalScale={0} displayType="text" />
                    <DifferenceComponent stat={stats?.avg_assists} classStat={classStats?.avg_assists} precision={0} fontSize={"0.8em"} iconSize={"1em"} />
                  </Box>
                </Box>
                <Box paddingBottom="5px" marginBottom="1em" display="grid" gridTemplateColumns="auto auto" color={fontColor} borderBottom={borderBottom}>
                  <Box fontWeight="bold">FINISHING BLOWS</Box>
                  <Box justifySelf="end" textAlign="end">
                    <NumberFormat value={(stats?.avg_fBlows || 0)} thousandSeparator={true} decimalScale={0} displayType="text" />
                    <DifferenceComponent stat={stats?.avg_fBlows} classStat={classStats?.avg_fBlows} precision={0} fontSize={"0.8em"} iconSize={"1em"} />
                  </Box>
                </Box>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={12} md={4}>
            <Paper>
              <Box display="grid" p={1} gridTemplateRows={"auto 1fr max-content max-content max-content"} minHeight={412}>
                <Box className={classes.cardTitle}>AVERAGE CROWD CONTROLS</Box>
                <Box paddingBottom="1em" fontWeight="bold" display="grid" gridTemplateColumns={"auto 1fr"} gridGap={"1em"}>
                  <Cross style={{width:60,height:60,fill:fontColor}} />
                  <Box>
                    <div style={{fontSize:'2em'}}><NumberFormat value={(stats?.avg_ccs || 0)} thousandSeparator={true} decimalScale={0} displayType="text" /></div>
                    <DifferenceComponent stat={stats?.avg_ccs} classStat={classStats?.avg_ccs} precision={0} fontSize={"1.4em"} iconSize={"1em"} />
                  </Box>
                </Box>
                <Box paddingBottom="5px" marginBottom="1em" display="grid" gridTemplateColumns="auto auto" color={fontColor} borderBottom={borderBottom}>
                  <Box fontWeight="bold">CCS</Box>
                  <Box justifySelf="end" textAlign="end">
                    <NumberFormat value={(stats?.avg_ccs || 0)} thousandSeparator={true} decimalScale={0} displayType="text" />
                    <DifferenceComponent stat={stats?.avg_ccs} classStat={classStats?.avg_ccs} precision={0} fontSize={"0.8em"} iconSize={"1em"} />
                  </Box>
                </Box>
                <Box paddingBottom="5px" marginBottom="1em" display="grid" gridTemplateColumns="auto auto" color={fontColor} borderBottom={borderBottom}>
                  <Box fontWeight="bold">OFFENSIVE CCS</Box>
                  <Box justifySelf="end" textAlign="end">
                    <NumberFormat value={(stats?.avg_oCCs || 0)} thousandSeparator={true} decimalScale={0} displayType="text" />
                    <DifferenceComponent stat={stats?.avg_oCCs} classStat={classStats?.avg_oCCs} precision={0} fontSize={"0.8em"} iconSize={"1em"} />
                  </Box>
                </Box>
                <Box paddingBottom="5px" marginBottom="1em" display="grid" gridTemplateColumns="auto auto" color={fontColor} borderBottom={borderBottom}>
                  <Box fontWeight="bold">DEFENSIVE CCS</Box>
                  <Box justifySelf="end" textAlign="end">
                    <NumberFormat value={(stats?.avg_dCCs || 0)} thousandSeparator={true} decimalScale={0} displayType="text" />
                    <DifferenceComponent stat={stats?.avg_dCCs} classStat={classStats?.avg_dCCs} precision={0} fontSize={"0.8em"} iconSize={"1em"} />
                  </Box>
                </Box>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={12} md={4}>
            <Paper>
              <Box display="grid" p={1} gridTemplateRows={"auto 1fr auto auto auto auto"} minHeight={412}>
                <Box className={classes.cardTitle}>AVERAGE BURST</Box>
                <Box paddingBottom="1em" fontWeight="bold" display="grid" gridTemplateColumns={"auto 1fr"} gridGap={"1em"}>
                  <Crosshair style={{width:60,height:60,fill:fontColor}} />
                  <Box>
                    <div style={{fontSize:'2em'}}><NumberFormat value={(stats?.avg_peakburst || 0)} thousandSeparator={true} decimalScale={0} displayType="text" /></div>
                    <DifferenceComponent stat={stats?.avg_peakburst} classStat={classStats?.avg_peakburst} precision={0} fontSize={"1.4em"} iconSize={"1em"} />
                  </Box>
                </Box>
                <Box paddingBottom="5px" marginBottom="1em" display="grid" gridTemplateColumns="auto auto" color={fontColor} borderBottom={borderBottom}>
                  <Box fontWeight="bold">PEAK BURST</Box>
                  <Box justifySelf="end" textAlign="end">
                    <NumberFormat value={(stats?.avg_peakburst || 0)} thousandSeparator={true} decimalScale={0} displayType="text" />
                    <DifferenceComponent stat={stats?.avg_peakburst} classStat={classStats?.avg_peakburst} precision={0} fontSize={"0.8em"} iconSize={"1em"} />
                  </Box>
                </Box>
                <Box paddingBottom="5px" marginBottom="1em" display="grid" gridTemplateColumns="auto auto" color={fontColor} borderBottom={borderBottom}>
                  <Box fontWeight="bold">AVG BURST</Box>
                  <Box justifySelf="end" textAlign="end">
                    <NumberFormat value={(stats?.avg_avgburst || 0)} thousandSeparator={true} decimalScale={0} displayType="text" />
                    <DifferenceComponent stat={stats?.avg_avgburst} classStat={classStats?.avg_avgburst} precision={0} fontSize={"0.8em"} iconSize={"1em"} />
                  </Box>
                </Box>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={12} md={6}>
            <Paper>
              <Box display="grid" p={1} gridTemplateRows={"auto 1fr auto auto auto"}>
                <Box className={classes.cardTitle}>TOP DPS</Box>
                <ReactResizeDetector handleWidth handleHeight>
                  {({width}:{width:number,height:number})=>
                    <Box justifySelf="center">
                      <CircularProgressWithLabel thickness={1.5} size={200} value={(normalize(stats?.top_dps || 0,0,(classStats?.top_dps || 0)) * 100).clamp(0,100)}>
                        <div style={{textAlign:'center'}}>
                          <div style={{fontSize:'3em'}}>{(stats?.top_dps || 0).toFixed(2)}</div>
                          <DifferenceComponent stat={stats?.top_dps} classStat={classStats?.top_dps} precision={0} fontSize={"1.4em"} iconSize={"1em"} />
                        </div>
                      </CircularProgressWithLabel>
                    </Box>
                  }
                </ReactResizeDetector>
                <Box paddingBottom="5px" marginBottom="1em" display="grid" gridTemplateColumns="auto auto" color={fontColor} borderBottom={borderBottom}>
                  <Box fontWeight="bold">DAMAGE</Box>
                  <Box justifySelf="end" textAlign="end">
                    <NumberFormat value={(stats?.top_damage || 0)} thousandSeparator={true} decimalScale={0} displayType="text" />
                    <DifferenceComponent stat={stats?.top_damage} classStat={classStats?.top_damage} precision={0} fontSize={"0.8em"} iconSize={"1em"} />
                  </Box>
                </Box>
                <Box paddingBottom="5px" marginBottom="1em" display="grid" gridTemplateColumns="auto auto" color={fontColor} borderBottom={borderBottom}>
                  <Box fontWeight="bold">DAMAGE TAKEN</Box>
                  <Box justifySelf="end" textAlign="end">
                    <NumberFormat value={(stats?.top_dTaken || 0)} thousandSeparator={true} decimalScale={0} displayType="text" />
                    <DifferenceComponent stat={stats?.top_dTaken} classStat={classStats?.top_dTaken} precision={0} fontSize={"0.8em"} iconSize={"1em"} />
                  </Box>
                </Box>
                <Box paddingBottom="5px" marginBottom="1em" display="grid" gridTemplateColumns="auto auto" color={fontColor} borderBottom={borderBottom}>
                  <Box fontWeight="bold">DAMAGE TAKEN PER SECOND</Box>
                  <Box justifySelf="end" textAlign="end">
                    <NumberFormat value={(stats?.top_dtps || 0)} thousandSeparator={true} decimalScale={2} displayType="text" />
                    <DifferenceComponent stat={stats?.top_dtps} classStat={classStats?.top_dtps} precision={2} fontSize={"0.8em"} iconSize={"1em"} />
                  </Box>
                </Box>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={12} md={6}>
            <Paper>
              <Box display="grid" p={1} gridTemplateRows={"auto 1fr auto auto"}>
                <Box className={classes.cardTitle}>TOP HPS</Box>
                <ReactResizeDetector handleWidth handleHeight>
                  {({width}:{width:number,height:number})=>
                    <Box justifySelf="center">
                      <CircularProgressWithLabel thickness={1.5} size={200} value={(normalize(stats?.top_hps || 0,0,(classStats?.top_hps || 0)) * 100).clamp(0,100)}>
                        <div style={{textAlign:'center'}}>
                          <div style={{fontSize:'3em'}}>{(stats?.top_hps || 0).toFixed(2)}</div>
                          <DifferenceComponent stat={stats?.top_hps} classStat={classStats?.top_hps} precision={0} fontSize={"1.4em"} iconSize={"1em"} />
                        </div>
                      </CircularProgressWithLabel>
                    </Box>
                  }
                </ReactResizeDetector>
                <Box paddingBottom="5px" marginBottom="1em" display="grid" gridTemplateColumns="auto auto" color={fontColor} borderBottom={borderBottom}>
                  <Box fontWeight="bold">Healing</Box>
                  <Box justifySelf="end" textAlign="end">
                    <NumberFormat value={(stats?.top_heal || 0)} thousandSeparator={true} decimalScale={0} displayType="text" />
                    <DifferenceComponent stat={stats?.top_heal} classStat={classStats?.top_heal} precision={0} fontSize={"0.8em"} iconSize={"1em"} />
                  </Box>
                </Box>
                <Box paddingBottom="5px" marginBottom="1em" display="grid" gridTemplateColumns="auto auto" color={fontColor} borderBottom={borderBottom}>
                  <Box fontWeight="bold">Effective Big Heals</Box>
                  <Box justifySelf="end" textAlign="end">
                    <NumberFormat value={(stats?.top_eBigHeal || 0)} thousandSeparator={true} decimalScale={0} displayType="text" />
                    <DifferenceComponent stat={stats?.top_eBigHeal} classStat={classStats?.top_eBigHeal} precision={0} fontSize={"0.8em"} iconSize={"1em"} />
                  </Box>
                </Box>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={12} md={4}>
            <Paper>
              <Box display="grid" p={1} gridTemplateRows={"auto 1fr auto auto auto auto auto"} minHeight={412}>
                <Box className={classes.cardTitle}>TOP K/D</Box>
                <Box paddingBottom="1em" fontWeight="bold" display="grid" gridTemplateColumns={"auto 1fr"} gridGap={"1em"}>
                  <Sword style={{width:60,fill:fontColor}} />
                  <Box>
                    <div style={{fontSize:'2em'}}><NumberFormat value={(stats?.top_game_kd || 0)} thousandSeparator={true} decimalScale={2} displayType="text" /></div>
                    <DifferenceComponent stat={stats?.top_game_kd} classStat={classStats?.top_game_kd} precision={2} fontSize={"1.4em"} iconSize={"1em"} />
                  </Box>
                </Box>
                <Box paddingBottom="5px" marginBottom="1em" display="grid" gridTemplateColumns="auto auto" color={fontColor} borderBottom={borderBottom}>
                  <Box fontWeight="bold">EFFECTIVE KILLS</Box>
                  <Box justifySelf="end" textAlign="end">
                    <NumberFormat value={(stats?.top_eKills || 0)} thousandSeparator={true} decimalScale={0} displayType="text" />
                    <DifferenceComponent stat={stats?.top_eKills} classStat={classStats?.top_eKills} precision={0} fontSize={"0.8em"} iconSize={"1em"} />
                  </Box>
                </Box>
                <Box paddingBottom="5px" marginBottom="1em" display="grid" gridTemplateColumns="auto auto" color={fontColor} borderBottom={borderBottom}>
                  <Box fontWeight="bold">KILLS</Box>
                  <Box justifySelf="end" textAlign="end">
                    <NumberFormat value={(stats?.top_kills || 0)} thousandSeparator={true} decimalScale={0} displayType="text" />
                    <DifferenceComponent stat={stats?.top_kills} classStat={classStats?.top_kills} precision={0} fontSize={"0.8em"} iconSize={"1em"} />
                  </Box>
                </Box>
                <Box paddingBottom="5px" marginBottom="1em" display="grid" gridTemplateColumns="auto auto" color={fontColor} borderBottom={borderBottom}>
                  <Box fontWeight="bold">DEATHS</Box>
                  <Box justifySelf="end" textAlign="end">
                    <NumberFormat value={(stats?.top_deaths || 0)} thousandSeparator={true} decimalScale={0} displayType="text" />
                    <DifferenceComponent stat={stats?.top_deaths} classStat={classStats?.top_deaths} precision={0} fontSize={"0.8em"} iconSize={"1em"} />
                  </Box>
                </Box>
                <Box paddingBottom="5px" marginBottom="1em" display="grid" gridTemplateColumns="auto auto" color={fontColor} borderBottom={borderBottom}>
                  <Box fontWeight="bold">ASSISTS</Box>
                  <Box justifySelf="end" textAlign="end">
                    <NumberFormat value={(stats?.top_assists || 0)} thousandSeparator={true} decimalScale={0} displayType="text" />
                    <DifferenceComponent stat={stats?.top_assists} classStat={classStats?.top_assists} precision={0} fontSize={"0.8em"} iconSize={"1em"} />
                  </Box>
                </Box>
                <Box paddingBottom="5px" marginBottom="1em" display="grid" gridTemplateColumns="auto auto" color={fontColor} borderBottom={borderBottom}>
                  <Box fontWeight="bold">FINISHING BLOWS</Box>
                  <Box justifySelf="end" textAlign="end">
                    <NumberFormat value={(stats?.top_fBlows || 0)} thousandSeparator={true} decimalScale={0} displayType="text" />
                    <DifferenceComponent stat={stats?.top_fBlows} classStat={classStats?.top_fBlows} precision={0} fontSize={"0.8em"} iconSize={"1em"} />
                  </Box>
                </Box>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={12} md={4}>
            <Paper>
              <Box display="grid" p={1} gridTemplateRows={"auto 1fr max-content max-content max-content"} minHeight={412}>
                <Box className={classes.cardTitle}>TOP CROWD CONTROLS</Box>
                <Box paddingBottom="1em" fontWeight="bold" display="grid" gridTemplateColumns={"auto 1fr"} gridGap={"1em"}>
                  <Cross style={{width:60,height:60,fill:fontColor}} />
                  <Box>
                    <div style={{fontSize:'2em'}}><NumberFormat value={(stats?.top_ccs || 0)} thousandSeparator={true} decimalScale={0} displayType="text" /></div>
                    <DifferenceComponent stat={stats?.top_ccs} classStat={classStats?.top_ccs} precision={0} fontSize={"1.4em"} iconSize={"1em"} />
                  </Box>
                </Box>
                <Box paddingBottom="5px" marginBottom="1em" display="grid" gridTemplateColumns="auto auto" color={fontColor} borderBottom={borderBottom}>
                  <Box fontWeight="bold">CCS</Box>
                  <Box justifySelf="end" textAlign="end">
                    <NumberFormat value={(stats?.top_ccs || 0)} thousandSeparator={true} decimalScale={0} displayType="text" />
                    <DifferenceComponent stat={stats?.top_ccs} classStat={classStats?.top_ccs} precision={0} fontSize={"0.8em"} iconSize={"1em"} />
                  </Box>
                </Box>
                <Box paddingBottom="5px" marginBottom="1em" display="grid" gridTemplateColumns="auto auto" color={fontColor} borderBottom={borderBottom}>
                  <Box fontWeight="bold">OFFENSIVE CCS</Box>
                  <Box justifySelf="end" textAlign="end">
                    <NumberFormat value={(stats?.top_oCCs || 0)} thousandSeparator={true} decimalScale={0} displayType="text" />
                    <DifferenceComponent stat={stats?.top_oCCs} classStat={classStats?.top_oCCs} precision={0} fontSize={"0.8em"} iconSize={"1em"} />
                  </Box>
                </Box>
                <Box paddingBottom="5px" marginBottom="1em" display="grid" gridTemplateColumns="auto auto" color={fontColor} borderBottom={borderBottom}>
                  <Box fontWeight="bold">DEFENSIVE CCS</Box>
                  <Box justifySelf="end" textAlign="end">
                    <NumberFormat value={(stats?.top_dCCs || 0)} thousandSeparator={true} decimalScale={0} displayType="text" />
                    <DifferenceComponent stat={stats?.top_dCCs} classStat={classStats?.top_dCCs} precision={0} fontSize={"0.8em"} iconSize={"1em"} />
                  </Box>
                </Box>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={12} md={4}>
            <Paper>
              <Box display="grid" p={1} gridTemplateRows={"auto 1fr auto auto auto auto"} minHeight={412}>
                <Box className={classes.cardTitle}>TOP BURST</Box>
                <Box paddingBottom="1em" fontWeight="bold" display="grid" gridTemplateColumns={"auto 1fr"} gridGap={"1em"}>
                  <Crosshair style={{width:60,height:60,fill:fontColor}} />
                  <Box>
                    <div style={{fontSize:'2em'}}><NumberFormat value={(stats?.top_peakburst || 0)} thousandSeparator={true} decimalScale={0} displayType="text" /></div>
                    <DifferenceComponent stat={stats?.top_peakburst} classStat={classStats?.top_peakburst} precision={0} fontSize={"1.4em"} iconSize={"1em"} />
                  </Box>
                </Box>
                <Box paddingBottom="5px" marginBottom="1em" display="grid" gridTemplateColumns="auto auto" color={fontColor} borderBottom={borderBottom}>
                  <Box fontWeight="bold">PEAK BURST</Box>
                  <Box justifySelf="end" textAlign="end">
                    <NumberFormat value={(stats?.top_peakburst || 0)} thousandSeparator={true} decimalScale={0} displayType="text" />
                    <DifferenceComponent stat={stats?.top_peakburst} classStat={classStats?.top_peakburst} precision={0} fontSize={"0.8em"} iconSize={"1em"} />
                  </Box>
                </Box>
                <Box paddingBottom="5px" marginBottom="1em" display="grid" gridTemplateColumns="auto auto" color={fontColor} borderBottom={borderBottom}>
                  <Box fontWeight="bold">AVG BURST</Box>
                  <Box justifySelf="end" textAlign="end">
                    <NumberFormat value={(stats?.top_avgburst || 0)} thousandSeparator={true} decimalScale={0} displayType="text" />
                    <DifferenceComponent stat={stats?.top_avgburst} classStat={classStats?.top_avgburst} precision={0} fontSize={"0.8em"} iconSize={"1em"} />
                  </Box>
                </Box>
              </Box>
            </Paper>
          </Grid>

        </Grid>
        {gamesByUser != undefined && mapStats != undefined &&
          <Grid container item xs={12} sm={12} md={12} lg={4} spacing={2}>
            <Grid item xs={12} sm={12}>
              <Box className={classes.cardTitle}>MATCHES</Box>
              <Stepper classes={{root:classes.stepper}} style={{overflow:'auto'}} activeStep={_.take(gamesByUser?.games,6).length - 1}>
                {
                  _.reverse(_.take(gamesByUser?.games,6)).map((game,index)=>{
                    let zone = ZoneInformation[game.zone || ""] || null;
                    let currentMap = zone ? zone.mapInfo[game.map || ""] : null;
                    let image = game.zone && game.map ? MapThumbnailIcons[game.zone || ""][game.map || ""] : null;
                    let participant = game?.performance;
                    let winner = participant ? participant.team == game.winner : false;
                    return <Step key={index}>
                      <StepLabel StepIconComponent={(props)=>{
                        return (
                          <Box color={fontColor} fontSize={"0.75em"} display="grid" gridTemplateRows={"auto auto auto"} justifyItems="center" gridGap={"10px"}>
                            <img src={image} title={`${zone?.name}: ${currentMap?.name}`} style={{height:'50px',width:'50px',borderRadius:'50%',objectFit:'cover'}} />
                            <Box width={10} height={10} style={{background:winner ? "#97E668" : "#C72D2E", borderRadius:'100%'}}></Box>
                            {participant && <React.Fragment>
                              {participant.team == 0 ?
                                <Box>{(game.teams && game.teams[0]) && game.teams[0].score}:{(game.teams && game.teams[1]) && game.teams[1].score}</Box>
                              :
                                <Box>{(game.teams && game.teams[1]) && game.teams[1].score}:{(game.teams && game.teams[0]) && game.teams[0].score}</Box>
                              }
                            </React.Fragment>}
                          </Box>
                        )
                      }} />
                    </Step>
                  })
                }
              </Stepper>
            </Grid>
            <Grid item xs={12} sm={12} md={6}>
              <Paper>
                <Box style={{backgroundSize:'cover',backgroundImage:`url(${mostPlayedMap})`,backgroundRepeat:'50% 50%'}}>
                  <Box style={{background:'rgba(255,255,255,0.03)',borderRadius:6,height:'100%'}} p={1} display="grid" gridAutoRows={"auto"} gridGap="10px">
                    <Box>Most Played</Box>
                    {
                      _.take(_.reverse(_.sortBy(mapStats?.maps,(map)=>map.played)),10).map((map)=>{
                        let zone = ZoneInformation[map.zone || ""] || null;
                        let currentMap = zone ? zone.mapInfo[map.map || ""] : null;
                        let image = map.zone && map.map ? MapThumbnailIcons[map.zone || ""][map.map || ""] : null;
                        return <Box key={map.map} display={'grid'} gridTemplateRows={"auto auto"}>
                          <Box display="grid" gridTemplateColumns={"auto 1fr auto"} gridGap={'10px'} alignItems="center">
                            <img src={image} title={`${zone?.name}: ${currentMap?.name}`} style={{height:'30px',width:'30px',borderRadius:'50%',objectFit:'cover'}} />
                            {currentMap?.name}
                            <Box justifySelf="end">{map.played}</Box>
                          </Box>
                          <Box marginTop="10px">
                            <LinearProgress variant="determinate" classes={{root:classes.progress}} value={normalize(map.played || 0,0,(mapStats?.maps ? mapStats.maps[0].played || 0 : 1)) * 100} />
                          </Box>
                        </Box>
                      })
                    }
                  </Box>
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={12} md={6}>
              <Paper>
                <Box style={{backgroundSize:'cover',backgroundImage:`url(${mostSuccessfulMap})`,backgroundRepeat:'50% 50%'}}>
                  <Box style={{background:'rgba(255,255,255,0.03)',borderRadius:6,height:'100%'}} p={1} display="grid" gridAutoRows={"auto"} gridGap="10px">
                    <Box>Most Successfull</Box>
                    {
                      mostSuccessfulMaps.map((map)=>{
                        let zone = ZoneInformation[map.zone || ""] || null;
                        let currentMap = zone ? zone.mapInfo[map.map || ""] : null;
                        let image = map.zone && map.map ? MapThumbnailIcons[map.zone || ""][map.map || ""] : null;
                        return <Box key={map.map} display={'grid'} gridTemplateRows={"auto auto"}>
                          <Box display="grid" gridTemplateColumns={"auto 1fr auto"} gridGap={'10px'} alignItems="center">
                            <img src={image} title={`${zone?.name}: ${currentMap?.name}`} style={{height:'30px',width:'30px',borderRadius:'50%',objectFit:'cover'}} />
                            {currentMap?.name}
                            <Box justifySelf="end"><NumberFormat value={(map.wr || 0) * 100} decimalScale={0} displayType="text" />%</Box>
                          </Box>
                          <Box marginTop="10px">
                            <LinearProgress variant="determinate" classes={{root:classes.progress}} value={(map.wr || 0) * 100} />
                          </Box>
                        </Box>
                      })
                    }
                  </Box>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        }
      </Grid>
    </div>
	);
};

export {UserStats};